import { useEffect, useState } from 'react';
import type { DeployConfirmationProps } from '~/lib/types/netlify';
import { LinkButtons } from './NetlifySuccessDisplay';
import { netlifyStore, setIsCommunityApproved } from '~/lib/stores/netlify';
import CommunityModal from '~/components/modals/CommunityModal';
import { useStore } from '@nanostores/react';
import { Tooltip } from '~/components/ui/Tooltip';
import * as Sentry from '@sentry/remix';
import { useLoaderData } from '@remix-run/react';
import { getMessages } from '~/lib/persistence/db';
import { db, description } from '~/lib/persistence/useChatHistory';
import { userStore } from '~/lib/stores/user';

export const DeployConfirmation = ({
  hasExistingDeployment,
  previousDeployUrl,
  deploymentStatus,
  onRedeploy: onDeploy,
  adminUrl,
  showPushSection = false,
  deployedId,
  accessToken,
  isBackgroundRedeploy = false,
}: DeployConfirmationProps) => {
  const storeState = useStore(netlifyStore);
  const { id: mixedId } = useLoaderData<{ id?: string }>();
  const user = useStore(userStore);
  const projectDescription = useStore(description);
  const [prompt, setPrompt] = useState<string>('');

  // Fetch community approval status on mount (or when previousDeployUrl changes)
  useEffect(() => {
    if (!previousDeployUrl) {
      return;
    }

    (async () => {
      try {
        const res = await fetch('/api/dualiteCommunity', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ op: 'get', url: previousDeployUrl }),
        });
        const { data } = (await res.json()) as { data: string | null };

        if (res.ok && data) {
          setIsCommunityApproved(true);
        }
      } catch (err) {
        console.error('Community check failed', err);
        Sentry.captureException(err, {
          extra: {
            message: 'Community check failed',
            url: previousDeployUrl,
          },
        });
      }
    })();
  }, [previousDeployUrl]);

  const [isCommunityModalOpen, setIsCommunityModalOpen] = useState(false);

  useEffect(() => {
    if (isCommunityModalOpen && db) {
      (async () => {
        const messages = await getMessages(db, mixedId as string);
        const firstUserMessage = messages?.messages.find(
          (message: { role: string }) => message.role === 'user',
        )?.content;
        setPrompt(firstUserMessage as string);
      })();
    }
  }, [isCommunityModalOpen, mixedId]);

  const getButtonLabel = () => {
    if (deploymentStatus === 'error') {
      return 'Try Again';
    } else if (deploymentStatus === 'ready' || hasExistingDeployment) {
      return 'Publish Again';
    } else {
      return 'Publish';
    }
  };

  const isLoading = deploymentStatus === 'new' || deploymentStatus === 'building';

  // const shouldShowFirstDeployMsg =
  //   !showPushSection && !hasExistingDeployment && !previousDeployUrl && deploymentStatus !== 'ready';
  const shouldShowPushSection = showPushSection || hasExistingDeployment || deploymentStatus === 'ready';
  const deployedUrl = storeState.deployedUrl ?? previousDeployUrl;

  return (
    <div className="space-y-4">
      {/* {shouldShowFirstDeployMsg && (
        <div className="text-[#888888] text-sm mt-2">Are you sure you want to deploy your site to Netlify?</div>
      )} */}

      {hasExistingDeployment && previousDeployUrl && deploymentStatus !== 'ready' && !isBackgroundRedeploy && (
        <div>
          <LinkButtons
            deployedUrl={previousDeployUrl}
            adminUrl={adminUrl ?? ''}
            deployedId={deployedId ?? ''}
            accessToken={accessToken ?? ''}
            deployStatus={deploymentStatus}
            fromRedeploy={true}
          />
        </div>
      )}

      <div className="h-px bg-white/10 my-2" />

      <div className="flex flex-col w-full gap-3">
        <button
          onClick={onDeploy}
          className="flex items-center justify-center w-full px-6 py-2.5 bg-white
          rounded-md text-black text-sm font-semibold hover:bg-[#e6e6e6] transition-all 
          disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          disabled={deploymentStatus === 'new' || deploymentStatus === 'building'}
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <span>Going Live</span>
              <div className="i-ph:circle-notch w-4 h-4 animate-spin" />
            </div>
          ) : (
            getButtonLabel()
          )}
        </button>

        {shouldShowPushSection && (
          <div className="flex w-full">
            <Tooltip
              content={
                storeState.isCommunityApproved
                  ? 'Project already submitted'
                  : 'You can submit your project to the Dualite Community'
              }
              className="w-full"
            >
              <button
                onClick={() => setIsCommunityModalOpen(true)}
                className="w-full text-white font-medium text-sm px-6 py-2.5 rounded-md border border-white/20 bg-transparent hover:bg-white/5 transition-all duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={storeState.isCommunityApproved}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>Submit to Showcase</span>
                </div>
              </button>
            </Tooltip>
          </div>
        )}
      </div>
      {shouldShowPushSection && (
        <>
          <div className="text-[#888888] text-xs mb-4 font-medium">
            <a
              href="https://alphadocs.dualite.dev/getting-started/showcase-by-dualite"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:text-white transition-colors"
            >
              Read more about Showcase by Dualite here
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 ml-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </>
      )}

      <CommunityModal
        isOpen={isCommunityModalOpen}
        onClose={() => setIsCommunityModalOpen(false)}
        deployedUrl={deployedUrl}
        screenShotUrl={storeState.screenshotUrl}
        projectName={projectDescription || 'Untitled'}
        projectDescription={projectDescription || ''}
        authorEmail={user.email}
        prompt={prompt}
      />
    </div>
  );
};
